import { useState, useEffect, useCallback, useMemo } from "react";
import "@/App.css";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, MapPin, Navigation, Beer, Store, X } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default center (New Jersey)
const DEFAULT_CENTER = [39.8336, -74.8710];
const DEFAULT_ZOOM = 8;

// Custom marker icons
const barIcon = new L.Icon({
  iconUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='40' viewBox='0 0 32 40'%3E%3Cpath fill='%23a9473d' d='M16 0C7.163 0 0 7.163 0 16c0 12 16 24 16 24s16-12 16-24c0-8.837-7.163-16-16-16z'/%3E%3Ccircle fill='white' cx='16' cy='14' r='8'/%3E%3C/svg%3E",
  iconSize: [32, 40],
  iconAnchor: [16, 40],
  popupAnchor: [0, -40],
});

const storeIcon = new L.Icon({
  iconUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='40' viewBox='0 0 32 40'%3E%3Cpath fill='%2356b6b2' d='M16 0C7.163 0 0 7.163 0 16c0 12 16 24 16 24s16-12 16-24c0-8.837-7.163-16-16-16z'/%3E%3Ccircle fill='white' cx='16' cy='14' r='8'/%3E%3C/svg%3E",
  iconSize: [32, 40],
  iconAnchor: [16, 40],
  popupAnchor: [0, -40],
});

const userIcon = new L.Icon({
  iconUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Ccircle fill='%23ccb64c' cx='12' cy='12' r='10' stroke='white' stroke-width='3'/%3E%3C/svg%3E",
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

// Component to handle map view changes
function MapController({ center, zoom }) {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, zoom, { animate: true });
    }
  }, [center, zoom, map]);
  
  return null;
}

function BeerFinder() {
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [searchAddress, setSearchAddress] = useState("");
  const [locationType, setLocationType] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [showMobileList, setShowMobileList] = useState(false);

  // Load all locations on mount
  useEffect(() => {
    loadLocations();
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (e) {
      console.error("Failed to load stats:", e);
    }
  };

  const loadLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
      setFilteredLocations(response.data);
    } catch (e) {
      console.error("Failed to load locations:", e);
      setError("Failed to load locations");
    }
  };

  const handleSearch = async () => {
    if (!searchAddress.trim()) {
      setError("Please enter an address or zip code");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First try to geocode the address
      const geocodeResponse = await axios.post(
        `${API}/geocode?address=${encodeURIComponent(searchAddress)}`
      );
      
      const newUserLocation = [
        geocodeResponse.data.latitude,
        geocodeResponse.data.longitude,
      ];
      setUserLocation(newUserLocation);
      setMapCenter(newUserLocation);
      setMapZoom(11);

      // Get all locations and calculate distances
      const response = await axios.post(`${API}/locations/search`, {
        latitude: geocodeResponse.data.latitude,
        longitude: geocodeResponse.data.longitude,
        radius: 99999,  // Very large radius to get all locations
        location_type: locationType === "all" ? null : locationType,
      });

      setFilteredLocations(response.data);
      
    } catch (e) {
      console.error("Search failed:", e);
      // Don't show error - just show all locations without distance sorting
      setFilteredLocations(locations);
      setUserLocation(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = useCallback((newType) => {
    setLocationType(newType);
    if (userLocation) {
      // Re-search with new filter
      axios.post(`${API}/locations/search`, {
        latitude: userLocation[0],
        longitude: userLocation[1],
        radius: 99999,
        location_type: newType === "all" ? null : newType,
      }).then(response => {
        setFilteredLocations(response.data);
      }).catch(console.error);
    } else {
      // Just filter locally
      let filtered = [...locations];
      if (newType !== "all") {
        filtered = filtered.filter((loc) => loc.location_type === newType);
      }
      setFilteredLocations(filtered);
    }
  }, [locations, userLocation]);

  const handleLocationClick = (location) => {
    setSelectedLocation(location);
    if (location.latitude && location.longitude) {
      setMapCenter([location.latitude, location.longitude]);
      setMapZoom(14);
    }
    setShowMobileList(false);
  };

  const getDirections = (location) => {
    const destination = `${location.latitude},${location.longitude}`;
    const origin = userLocation
      ? `${userLocation[0]},${userLocation[1]}`
      : "";
    const url = origin
      ? `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`
      : `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
    window.open(url, "_blank");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchAddress("");
    setUserLocation(null);
    setFilteredLocations(locations);
    setMapCenter(DEFAULT_CENTER);
    setMapZoom(DEFAULT_ZOOM);
    setError(null);
    setSelectedLocation(null);
  };

  // Memoize markers for performance
  const markers = useMemo(() => {
    return filteredLocations
      .filter((loc) => loc.latitude && loc.longitude)
      .map((location) => (
        <Marker
          key={location.id}
          position={[location.latitude, location.longitude]}
          icon={location.location_type === "bar" ? barIcon : storeIcon}
          eventHandlers={{
            click: () => setSelectedLocation(location),
          }}
        >
          <Popup>
            <div className="leaflet-popup-content-custom">
              <h3 className="popup-title">{location.name}</h3>
              <p className="popup-address">
                {location.address}<br />
                {location.city}, {location.state} {location.zip_code}
              </p>
              {location.distance !== undefined && (
                <p className="popup-distance">{location.distance} miles away</p>
              )}
              <button
                className="popup-directions-btn"
                onClick={() => getDirections(location)}
              >
                <Navigation size={14} />
                Get Directions
              </button>
            </div>
          </Popup>
        </Marker>
      ));
  }, [filteredLocations]);

  return (
    <div className="beer-finder-container" data-testid="beer-finder">
      {/* Search Bar */}
      <div className="search-panel glass-panel" data-testid="search-panel">
        <div className="search-row">
          <div className="search-input-wrapper">
            <Search className="search-icon" size={18} />
            <Input
              data-testid="search-input"
              type="text"
              placeholder="Enter your address or zip code"
              value={searchAddress}
              onChange={(e) => setSearchAddress(e.target.value)}
              onKeyPress={handleKeyPress}
              className="search-input"
            />
            {searchAddress && (
              <button
                className="clear-btn"
                onClick={clearSearch}
                data-testid="clear-search-btn"
              >
                <X size={16} />
              </button>
            )}
          </div>

          <Button
            data-testid="search-btn"
            onClick={handleSearch}
            disabled={loading}
            className="search-btn"
          >
            {loading ? "Searching..." : "Search"}
          </Button>
        </div>

        <div className="filters-row">
          <div className="filter-group">
            <span className="filter-label">Show:</span>
            <Select
              value={locationType}
              onValueChange={handleFilterChange}
              data-testid="type-filter"
            >
              <SelectTrigger className="filter-select" data-testid="type-filter-trigger">
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="filter-all">All Locations</SelectItem>
                <SelectItem value="bar" data-testid="filter-bars">Bars Only</SelectItem>
                <SelectItem value="store" data-testid="filter-stores">Stores Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {error && <p className="error-message" data-testid="error-message">{error}</p>}
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Location List - Desktop */}
        <div className="list-panel glass-panel desktop-only" data-testid="location-list">
          <div className="list-header">
            <h2 className="list-title">Nearby Locations</h2>
            <span className="location-count" data-testid="location-count">
              {filteredLocations.length} locations found
            </span>
          </div>

          <ScrollArea className="location-scroll">
            {filteredLocations.length === 0 ? (
              <div className="no-results" data-testid="no-results">
                <MapPin size={48} className="no-results-icon" />
                <p>Enter your location to find nearby locations</p>
              </div>
            ) : (
              <div className="location-list">
                {filteredLocations.map((location) => (
                  <LocationCard
                    key={location.id}
                    location={location}
                    isSelected={selectedLocation?.id === location.id}
                    onClick={() => handleLocationClick(location)}
                    onGetDirections={() => getDirections(location)}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Map */}
        <div className="map-panel" data-testid="map-panel">
          <MapContainer
            center={DEFAULT_CENTER}
            zoom={DEFAULT_ZOOM}
            style={{ width: "100%", height: "100%" }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapController center={mapCenter} zoom={mapZoom} />
            
            {/* User location marker */}
            {userLocation && (
              <Marker position={userLocation} icon={userIcon}>
                <Popup>Your Location</Popup>
              </Marker>
            )}

            {/* Location markers */}
            {markers}
          </MapContainer>

          {/* Mobile List Toggle */}
          <button
            className="mobile-list-toggle mobile-only"
            onClick={() => setShowMobileList(!showMobileList)}
            data-testid="mobile-list-toggle"
          >
            {showMobileList ? "Show Map" : `Show List (${filteredLocations.length})`}
          </button>
        </div>

        {/* Mobile List Overlay */}
        {showMobileList && (
          <div className="mobile-list-overlay mobile-only" data-testid="mobile-list">
            <div className="mobile-list-header">
              <h2>Nearby Locations</h2>
              <button onClick={() => setShowMobileList(false)}>
                <X size={24} />
              </button>
            </div>
            <ScrollArea className="mobile-location-scroll">
              {filteredLocations.map((location) => (
                <LocationCard
                  key={location.id}
                  location={location}
                  isSelected={selectedLocation?.id === location.id}
                  onClick={() => handleLocationClick(location)}
                  onGetDirections={() => getDirections(location)}
                />
              ))}
            </ScrollArea>
          </div>
        )}
      </div>

      {/* Stats Footer */}
      {stats && (
        <div className="stats-footer glass-panel" data-testid="stats-footer">
          <span>{stats.bars} Bars</span>
          <span className="stats-divider">•</span>
          <span>{stats.stores} Stores</span>
        </div>
      )}
    </div>
  );
}

function LocationCard({ location, isSelected, onClick, onGetDirections }) {
  return (
    <div
      className={`location-card ${isSelected ? "selected" : ""}`}
      onClick={onClick}
      data-testid={`location-card-${location.id}`}
    >
      <div className="card-header">
        <div className={`card-icon ${location.location_type === "bar" ? "icon-bar-bg" : "icon-store-bg"}`}>
          {location.location_type === "bar" ? (
            <Beer size={20} className="icon-bar" />
          ) : (
            <Store size={20} className="icon-store" />
          )}
        </div>
        <div className="card-title-row">
          <h3 className="card-title">{location.name}</h3>
          {location.distance !== undefined && (
            <span className="distance-badge">{location.distance} mi</span>
          )}
        </div>
      </div>
      <p className="card-address">
        {location.address}
        <br />
        {location.city}, {location.state} {location.zip_code}
      </p>
      <div className="card-type">
        <span className={`type-badge ${location.location_type}`}>
          {location.location_type === "bar" ? "Bar" : "Store"}
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={(e) => {
          e.stopPropagation();
          onGetDirections();
        }}
        className="card-directions-btn"
        data-testid={`directions-btn-${location.id}`}
      >
        <Navigation size={14} />
        Directions
      </Button>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BeerFinder />
    </div>
  );
}

export default App;
